import React, { Component } from 'react';
import download from 'downloadjs';
import moment from 'moment';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { bindActionCreators } from 'redux';
import Error from 'shared/components/Error';
import { LoadingCard } from 'shared/components/Loading';
import { SubscriberType } from 'shared/models/Subscriber/constants';
import {
  billingDeleteCustomer,
  billingDeleteProject,
  invoiceGenerationStatus,
  generatePreflightExport,
  clearPreflightData
} from 'shared/store/modules/invoices/effects';
import { fetchInvoice } from 'shared/store/modules/subscribers/effects';
import { withInvoiceContext } from './InvoicesContext';
import InvoicesLayout from './InvoicesLayout';
import InvoicesStatus from './components/InvoicesStatus/InvoicesStatus';
import PreflightModal from './components/PreflightModal';
import ResultsTable from './components/ResultsTable';
import Progress from './components/ResultsTable/Progress';

class InvoicesShow extends Component {
  static displayName = 'InvoicesShowView';
  static defaultProps = {
    invoiceGenerationDetails: []
  };

  constructor(props) {
    super(props);

    this.intervalId = null;

    this.startPoll = this.startPoll.bind(this);
    this.clearPoll = this.clearPoll.bind(this);

    this.state = {
      updatedData: [],
      loading: false,
      totalInvoiceCount: this.props.data.invoiceGenerationDetails.length,
      showSubscriberInfo: [],
      billingId: this.props.data.billingId
    };
  }

  componentDidMount() {
    this.handleCheckStatus();
    if (this.props.data) {
      this.setState({
        updatedData: this.props.data.invoiceGenerationDetails,
        billingId: this.props.data.billingId,
        loading: false
      });
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    const { isProcessing } = this.props;
    if (isProcessing !== prevProps.isProcessing) {
      if (!isProcessing) {
        this.clearPoll();
      }

      if (isProcessing) {
        this.startPoll();
      }
    }

    if (prevProps.billingId !== this.props.data.billingId) {
      this.handleCheckStatus();
    }

    if (
      (prevProps.data.invoiceGenerationDetails.length === 0 && this.props.data.invoiceGenerationDetails.length > 0) ||
      prevProps.data.failedToGenerateInvoiceCount !== this.props.data.failedToGenerateInvoiceCount
    ) {
      this.setState({
        updatedData: this.props.data.invoiceGenerationDetails,
        totalInvoiceCount: this.props.data.totalInvoiceCount
      });
    }
  }

  handleDeleteProject = async (subscriberId, billingCustomerDetailProjectId, projectName, subscriberName) => {
    this.setState({ loading: true });
    try {
      await this.props.billingDeleteProject(billingCustomerDetailProjectId, projectName, subscriberName).then(data => {
        if (data.error) {
          console.error(data.error);
          this.setState({ loading: false });
        } else if (data.payload) {
          this.setState(prevState => {
            const updatedData = prevState.updatedData.map(item => {
              if (item.billingCustomerDetailId === subscriberId) {
                const updatedProjectDetails = item.projectDetails.filter(
                  project => project.id !== billingCustomerDetailProjectId
                );
                return {
                  ...item,
                  projectDetails: updatedProjectDetails
                };
              }
              return item;
            });

            return {
              updatedData,
              loading: false
            };
          });
        }
      });
    } catch (err) {
      this.setState({ loading: false });
      console.error(err);
    }
  };

  handleDeleteSubscriber = async (billingCustomerDetailId, displayName) => {
    this.setState({ loading: true });
    try {
      await this.props.billingDeleteCustomer(billingCustomerDetailId, displayName).then(data => {
        if (data.error) {
          console.error(data.error);
          this.setState({ loading: false });
        } else {
          this.setState(prevState => {
            const updatedData = prevState.updatedData.filter(
              item => item.billingCustomerDetailId !== billingCustomerDetailId
            );

            const filterInvoiceGenerationDetails = () => {
              return updatedData.filter(invoice => {
                const allStatusOne = invoice.projectDetails.every(project => project.status === 1);
                return !allStatusOne;
              });
            };

            const totalInvoiceCount = filterInvoiceGenerationDetails();

            return { updatedData, loading: false, totalInvoiceCount: totalInvoiceCount.length };
          });
        }
      });
    } catch (err) {
      this.setState({ loading: false });
      console.error(err);
    }
  };

  handleSubscriberDetailsToggle = id => {
    this.setState(prevState => {
      const toggleSubscriber = prevState.showSubscriberInfo.find(item => item.id === id);

      if (toggleSubscriber) {
        const updatedData = prevState.showSubscriberInfo.map(item =>
          item.id === id ? { ...item, open: !item.open } : item
        );
        return { showSubscriberInfo: updatedData };
      } else {
        return { showSubscriberInfo: [...prevState.showSubscriberInfo, { id, open: true }] };
      }
    });
    this.setState({ loading: false });
  };

  componentWillUnmount() {
    this.clearPoll();
  }

  UNSAFE_componentWillReceiveProps({ month, year, invoiceStatusExportData, preflightExportData }) {
    if (invoiceStatusExportData !== this.props.invoiceStatusExportData && invoiceStatusExportData) {
      download(invoiceStatusExportData, `${month}-${year}_Statement-Status-Export.csv`, 'text/csv');
    }

    if (preflightExportData !== this.props.preflightExportData && preflightExportData) {
      download(preflightExportData, `${month}-${year}_Preflight-Export.csv`, 'text/csv');
    }
  }

  startPoll() {
    this.intervalId = setInterval(this.handleCheckStatus, 5000);
  }

  clearPoll() {
    return clearInterval(this.intervalId);
  }

  handleCheckStatus = () => {
    const { actions, billingId } = this.props;
    actions.invoiceGenerationStatus(billingId);
  };

  handleDownloadPreflightExport = () => {
    const { actions, billingId } = this.props;
    actions.generatePreflightExport(billingId);
  };

  isFinalized() {
    return this.props.queuedCount === 0;
  }

  isReadyToGenerate() {
    return true;
  }

  renderResults = () => {
    const { month, year, isFetching, data, billing } = this.props;
    if (isFetching) return null;

    const {
      isProcessing,
      successfullySentCount,
      successfullyGeneratedInvoiceCount,
      filters,
      name,
      canFinalize,
      canGenerate,
      failedToGenerateInvoiceCount
    } = data;

    const { billingId } = data;

    const currentBatch = billing?.find(batch => billingId === batch.id);

    const invoicedBeforeLaunchDate = moment(currentBatch?.createdAt).isSameOrBefore(moment('03/06/2025'));

    if (isProcessing) {
      return (
        <>
          <InvoicesStatus
            name={name}
            subscriberData={this.state.updatedData}
            totalInvoiceCount={this.state.totalInvoiceCount}
            successfullySentCount={successfullySentCount}
            successfullyGeneratedInvoiceCount={successfullyGeneratedInvoiceCount}
            failedToGenerateInvoiceCount={failedToGenerateInvoiceCount}
            filters={filters}
            canFinalize={canFinalize}
            canGenerate={canGenerate}
            invoicedBeforeLaunchDate={invoicedBeforeLaunchDate}
          />
          <Progress {...data} />
        </>
      );
    }

    if (!!this.state.updatedData.length) {
      const commercialItems = this.state.updatedData.filter(batch => batch.customerType === SubscriberType.Commercial);
      const residentialItems = this.state.updatedData.filter(
        batch => batch.customerType === SubscriberType.Residential
      );

      const headings = (
        <>
          <th className="col-3">Customer Name</th>
          <th className="col-2 text-center">Projects</th>
          <th className="col-2 text-center">Total Invoice</th>
          <th className="col-2 text-center">Created</th>
          <th className="col-2 text-center">Sent</th>
          <th className="col-1">&nbsp;</th>
        </>
      );

      const searchKeys = ['displayName', 'projectDetails.projectName'];

      return (
        <>
          <InvoicesStatus
            name={name}
            subscriberData={this.state.updatedData}
            totalInvoiceCount={this.state.totalInvoiceCount}
            successfullySentCount={successfullySentCount}
            successfullyGeneratedInvoiceCount={successfullyGeneratedInvoiceCount}
            failedToGenerateInvoiceCount={failedToGenerateInvoiceCount}
            filters={filters}
            canFinalize={canFinalize}
            canGenerate={canGenerate}
            invoicedBeforeLaunchDate={invoicedBeforeLaunchDate}
          />
          <ResultsTable
            emptyText="No commerical subscribers included in this batch"
            headings={headings}
            icon="business"
            items={commercialItems}
            searchKeys={searchKeys}
            title="Commercial Subscribers"
            month={month}
            year={year}
            handleDeleteSubscriber={this.handleDeleteSubscriber}
            handleDeleteProject={this.handleDeleteProject}
            handleSubscriberDetailsToggle={this.handleSubscriberDetailsToggle}
            showSubscriberInfo={this.state.showSubscriberInfo}
            canFinalize={canFinalize}
            canGenerate={canGenerate}
            successfullySentCount={successfullySentCount}
            successfullyGeneratedInvoiceCount={successfullyGeneratedInvoiceCount}
            totalInvoiceCount={this.state.totalInvoiceCount}
            invoicedBeforeLaunchDate={invoicedBeforeLaunchDate}
          />
          <ResultsTable
            emptyText="No residential subscribers included in this batch"
            headings={headings}
            icon="home"
            items={residentialItems}
            searchKeys={searchKeys}
            title="Residential Subscribers"
            month={month}
            year={year}
            handleDeleteSubscriber={this.handleDeleteSubscriber}
            handleDeleteProject={this.handleDeleteProject}
            handleSubscriberDetailsToggle={this.handleSubscriberDetailsToggle}
            showSubscriberInfo={this.state.showSubscriberInfo}
            canFinalize={canFinalize}
            canGenerate={canGenerate}
            successfullySentCount={successfullySentCount}
            successfullyGeneratedInvoiceCount={successfullyGeneratedInvoiceCount}
            totalInvoiceCount={this.state.totalInvoiceCount}
            invoicedBeforeLaunchDate={invoicedBeforeLaunchDate}
          />
        </>
      );
    }

    return null;
  };

  render() {
    const { actions, isFetching, error, month, year, preflightCheck } = this.props;

    if (isFetching) return <LoadingCard className="mb-3" />;

    return (
      <>
        {preflightCheck && (
          <PreflightModal
            month={month}
            onClearPreflight={actions.clearPreflightData}
            onDownloadPreflight={this.handleDownloadPreflightExport}
            preflightCheck={preflightCheck}
            year={year}
          />
        )}
        {this.renderResults()}
        {error && !this.isReadyToGenerate() && <Error className="mt-3 mb-3" error={error} />}
      </>
    );
  }
}

const mapStateToProps = state => ({
  data: state.invoices.data,
  error: state.invoices.error,
  invoiceStatusExportData: state.invoices.invoiceStatusExportData,
  isDeleting: state.invoices.isDeleting,
  isFetching: state.invoices.isFetching,
  isProcessing: state.invoices.data.isProcessing,
  preflightCheck: state.invoices.preflightCheck,
  preflightExportData: state.invoices.preflightExportData
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchInvoice(id) {
    return dispatch(fetchInvoice(id));
  },
  billingDeleteCustomer(id, customerName) {
    return dispatch(billingDeleteCustomer(id, customerName));
  },
  billingDeleteProject(id, projectName, subscriberName) {
    return dispatch(billingDeleteProject(id, projectName, subscriberName));
  },
  actions: bindActionCreators(
    {
      invoiceGenerationStatus,
      generatePreflightExport,
      clearPreflightData
    },
    dispatch
  )
});

const ConnectedView = compose(withInvoiceContext, connect(mapStateToProps, mapDispatchToProps))(InvoicesShow);

export const InvoicesShowView = ({ error }) => (
  <InvoicesLayout error={error}>
    <ConnectedView />
  </InvoicesLayout>
);

export default InvoicesShowView;
